/**
 * PatientsPage.tsx
 *
 * Full patient list — search, sidebar filters, stat cards, paginated DataTable
 * with enriched columns (city/state, tags, referral source, coordinator),
 * row actions, CSV export, and deleted-patients section.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAISidebarStore } from '@/store/aiSidebarStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePatientNavigation } from '@/components/ui/PatientLink';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLink,
  Download,
  Megaphone,
  Plus,
} from 'lucide-react';
import { DetailProfile } from '@/components/layout/DetailProfile';
import { DetailRightPanel } from '@/components/layout/DetailRightPanel';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { PATIENT_SECTIONS, usePatientSections } from '@/hooks/usePatientSections';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableAction, DataTableColumn } from '@/components/ui/DataTable';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { PatientAvatar } from '@/components/patients/PatientAvatar';
import { TagIconRow } from '@/components/tags/TagIconBadge';
import { getPatients, getPatientKpis } from '@/services/patients.service';
import { usePatientFilterOptions } from '@/hooks/filters';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import { toast } from '@/store/toastStore';
import { prefetchPatientSidebar, usePatientDetail } from '@/hooks/usePatientDetail';
import { usePageContext } from '@/hooks/usePageContext';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { cn, parseDateValue } from '@/lib/utils';
import type { PatientSummary, PatientStatus, PatientListParams } from '@/types/patient.types';
import { isDateRange } from '@/types/filters.types';
import { buildApiParams, buildMarketingFilterConditions, extractSearch } from '@/lib/filterRegistry';
import { MarketingActionsModal } from '@/components/marketing/MarketingActionsModal';
import { useAuth } from '@/hooks/useAuth';
import {
  useFacilityOverrideValues,
  useLocalDashboardFilters,
} from '@/pages/dashboard/dashboardFilters';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDob(dateStr: string | null): JSX.Element | string {
  const d = parseDateValue(dateStr);
  if (!d) return '—';
  const ageDiff = Date.now() - d.getTime();
  const age = Math.floor(ageDiff / (365.25 * 24 * 60 * 60 * 1000));
  const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const year = d.getFullYear();
  return (
    <span className="whitespace-nowrap text-xs">
      <span className="text-slate-700">{monthDay}, {year}</span>
      <span className="text-slate-400"> / </span>
      <span className="font-semibold text-slate-700">{age}</span>
    </span>
  );
}

const currencyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length > 10) {
    const main = digits.startsWith('1') ? digits.slice(1, 11) : digits.slice(0, 10);
    const ext = digits.startsWith('1') ? digits.slice(11) : digits.slice(10);
    const formatted = `(${main.slice(0, 3)}) ${main.slice(3, 6)}-${main.slice(6)}`;
    return ext ? `${formatted} x${ext}` : formatted;
  }
  return raw;
}

const STATUS_STYLES: Record<PatientStatus, string> = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-slate-100 text-slate-500',
  deleted: 'bg-red-100 text-red-700',
  lead: 'bg-blue-100 text-blue-700',
};


import { buildPatientFilterConfigs } from './patientsShared';

// ─── Component ────────────────────────────────────────────────────────────────

export function PatientsPage() {
  const navigate = useNavigate();
  // Surfaces this practice switched off in the Patients module cog.
  const sections = usePatientSections();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const navToPatient = usePatientNavigation();
  const updateAIContext = usePageContext('Patients');
  const urlSearch = searchParams.get('search')?.trim() ?? '';
  const [selectedPatient, setSelectedPatient] = useState<PatientSummary | null>(null);
  const [panelExpandTrigger, setPanelExpandTrigger] = useState(0);
  const { detail, loading: detailLoading } = usePatientDetail(
    selectedPatient ? String(selectedPatient.patientId) : null,
  );

  const { roles } = useRoleTitles();
  const coordLabel = roles.find((r) => r.roleType === 'Coordinator')?.roleTitle || 'Coordinator';

  const filterConfigs = useMemo(
    () => buildPatientFilterConfigs(coordLabel),
    [coordLabel],
  );

  const navigateToPatient = useCallback((patient: Pick<PatientSummary, 'patientId' | 'displayName' | 'photoUrl'>) => {
    if (!patient.patientId) {
      toast.error('Unable to open patient', 'This row is missing a patient ID.');
      return;
    }

    navToPatient({
      patientId: patient.patientId,
      name: patient.displayName,
      photoUrl: patient.photoUrl,
    });
  }, [navToPatient]);

  const setAIContext = useAISidebarStore((s) => s.setContext);
  const queryClient = useQueryClient();

  const selectPatient = useCallback((patient: PatientSummary) => {
    if (!patient.patientId) {
      toast.error('Unable to open patient', 'This row is missing a patient ID.');
      return;
    }

    setSelectedPatient(patient);
    setPanelExpandTrigger((trigger) => trigger + 1);
    setAIContext('patient', patient.patientId);
  }, [setAIContext]);

  // Hover prefetch: warm the AI summary + core + stats queries when the user
  // hovers a patient row. A short debounce avoids firing on quick mouse passes.
  const prefetchTimerRef = useRef<number | null>(null);
  const prefetchPatientRow = useCallback((patient: PatientSummary) => {
    if (!patient.patientId) return;
    if (prefetchTimerRef.current !== null) {
      window.clearTimeout(prefetchTimerRef.current);
    }
    prefetchTimerRef.current = window.setTimeout(() => {
      prefetchPatientSidebar(queryClient, patient.patientId);
    }, 80);
  }, [queryClient]);

  useEffect(() => {
    return () => {
      if (prefetchTimerRef.current !== null) {
        window.clearTimeout(prefetchTimerRef.current);
      }
    };
  }, []);

  // ── Filters (shared with /home dashboard via storage key 'patients') ─────
  const filterState = useLocalDashboardFilters(filterConfigs, 'patients');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);
  const filterParams = useMemo(
    () => buildApiParams(effectiveValues, filterState.negations),
    [effectiveValues, filterState.negations],
  );
  const search = extractSearch(effectiveValues);

  // Honor `?search=` deep-link by writing the query into the shared filter bucket
  // exactly once per URL change.
  useEffect(() => {
    filterState.setFilter('search', urlSearch ? [urlSearch] : []);
  }, [urlSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sorting ──────────────────────────────────────────────────────────────
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortBy(key);
    setSortDir(direction);
    setPage(1);
  }, []);

  // ── Pagination ────────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const perPage = 100;

  // ── Deleted patients ──────────────────────────────────────────────────────
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedPage, setDeletedPage] = useState(1);
  const [marketingModalOpen, setMarketingModalOpen] = useState(false);
  const { exporting, startExport, exportModal } = useAsyncExport({ entityType: 'patients', entityLabel: 'patients' });

  // Reset page when filters change (both the active and deleted lists)
  useEffect(() => {
    setPage(1);
    setDeletedPage(1);
  }, [filterParams]);

  const marketingFilterConditions = useMemo(
    () => buildMarketingFilterConditions('patient', effectiveValues, filterState.negations),
    [effectiveValues, filterState.negations],
  );

  // ── Build filter params ───────────────────────────────────────────────────
  const filters: PatientListParams = useMemo(() => {
    const next: PatientListParams = { ...filterParams, page, limit: perPage };
    if (sortBy) {
      next.sortBy = sortBy;
      next.sortDir = sortDir;
    }
    return next;
  }, [filterParams, page, perPage, sortBy, sortDir]);

  // ── Fetch patients ────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['patients', filters],
    queryFn: () => getPatients(filters),
    staleTime: 30_000,
  });

  const patients: PatientSummary[] = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totalPages = Math.ceil(total / perPage) || 1;

  function handleExport() {
    startExport(filterParams);
  }

  // ── Deleted patients query ────────────────────────────────────────────────
  // Reuse the active-list filters (search + Filters panel + sort) so the
  // deleted view honors the same controls; the backend applies them to the
  // deleted branch when status=deleted. Override status + page only.
  const deletedFilters: PatientListParams = useMemo(
    () => ({ ...filters, status: 'deleted', page: deletedPage }),
    [filters, deletedPage],
  );

  const { data: deletedData, isLoading: isDeletedLoading } = useQuery({
    queryKey: ['patients-deleted', deletedFilters],
    queryFn: () => getPatients(deletedFilters),
    enabled: showDeleted,
    staleTime: 30_000,
  });

  // ── KPI counts ───────────────────────────────────────────────────────────
  const { data: kpis } = useQuery({
    queryKey: ['patients-kpis'],
    queryFn: getPatientKpis,
    staleTime: 60_000,
  });

  // ── Sidebar filter plumbing ───────────────────────────────────────────────

  const sidebarOptions = usePatientFilterOptions();

  // ── AI Page Context ──────────────────────────────────────────────────
  useEffect(() => {
    const activeFilterSummary = Object.entries(effectiveValues)
      .filter(([key, val]) => {
        if (key === 'search') return false;
        if (Array.isArray(val)) return val.length > 0;
        if (isDateRange(val)) return !!(val.from || val.to);
        return false;
      })
      .map(([key]) => key)
      .join(', ');

    updateAIContext({
      filters: activeFilterSummary || 'none (showing all)',
      dataSummary: `${patients.length} shown of ${total} total, page ${page}/${totalPages}${selectedPatient ? `, selected: ${selectedPatient.displayName}` : ''}`,
    });
  }, [effectiveValues, patients.length, total, page, totalPages, selectedPatient, updateAIContext]);

  // ── DataTable columns ─────────────────────────────────────────────────────

  const columns = useMemo<DataTableColumn<PatientSummary>[]>(() => {
    const all: DataTableColumn<PatientSummary>[] = [
    {
      key: 'displayName',
      header: 'Name',
      sortable: true,
      width: 'w-[280px]',
      render: (p) => (
        <div className="flex min-w-0 items-center gap-3">
          <PatientAvatar
            patientId={p.patientId}
            photoUrl={p.photoUrl}
            name={p.displayName}
            size="sm"
            className="flex-shrink-0"
          />
          <div className="min-w-0">
            <button
              type="button"
              className="block max-w-full truncate whitespace-nowrap font-medium text-blue-600 hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                selectPatient(p);
              }}
              title={p.displayName}
            >
              {p.displayName}
            </button>
            {p.displayId && (
              <div className="text-xs text-slate-400">{p.displayId}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      render: (p) => <span className="text-xs text-slate-600">{p.email ?? '—'}</span>,
    },
    {
      key: 'cellPhone',
      header: 'Phone',
      render: (p) =>
        p.cellPhone || p.homePhone ? (
          <div className="space-y-0.5 text-xs">
            {p.cellPhone && (
              <div className="flex items-center gap-1 whitespace-nowrap">
                <span className="inline-flex items-center justify-center rounded bg-blue-100 text-blue-700 text-[9px] font-bold w-3.5 h-3.5 flex-shrink-0">C</span>
                <span>{formatPhone(p.cellPhone)}</span>
              </div>
            )}
            {p.homePhone && (
              <div className="flex items-center gap-1 whitespace-nowrap">
                <span className="inline-flex items-center justify-center rounded bg-slate-100 text-slate-600 text-[9px] font-bold w-3.5 h-3.5 flex-shrink-0">H</span>
                <span>{formatPhone(p.homePhone)}</span>
              </div>
            )}
          </div>
        ) : (
          <span className="text-xs text-slate-400 whitespace-nowrap">{formatPhone(p.phone) || '—'}</span>
        ),
    },
    {
      key: 'dateOfBirth',
      header: 'DOB / Age',
      sortable: true,
      render: (p) => <span className="whitespace-nowrap">{formatDob(p.dateOfBirth)}</span>,
    },
    {
      key: 'city',
      header: 'City / State',
      sortable: true,
      render: (p) => (
        <span className="text-xs text-slate-600 whitespace-nowrap">
          {p.city || p.state ? [p.city, p.state].filter(Boolean).join(', ') : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (p) => (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize',
            STATUS_STYLES[p.status],
          )}
        >
          {p.status}
        </span>
      ),
    },
    {
      key: 'tags',
      header: 'Tags',
      render: (p) =>
        p.tags && p.tags.length > 0 ? (
          <TagIconRow tags={p.tags} variant="icon" gapClassName="gap-1" className="w-[148px]" />
        ) : (
          <span className="text-xs text-slate-400">—</span>
        ),
    },
    {
      key: 'referralSourceName',
      header: 'Referral Source',
      sortable: true,
      render: (p) => <span className="text-xs text-slate-600 whitespace-nowrap">{p.referralSourceName ?? '—'}</span>,
    },
    {
      key: 'totalSpent',
      header: 'Total Spent',
      sortable: true,
      align: 'right' as const,
      render: (p) => <span className="text-xs text-slate-600 whitespace-nowrap">{currencyFmt.format(p.totalSpent ?? 0)}</span>,
    },
    {
      key: 'balanceDue',
      header: 'Balance',
      sortable: true,
      align: 'right' as const,
      render: (p) => (
        <span className={(p.balanceDue ?? 0) > 0 ? 'text-xs text-red-600 font-medium whitespace-nowrap' : 'text-xs text-slate-600 whitespace-nowrap'}>
          {currencyFmt.format(p.balanceDue ?? 0)}
        </span>
      ),
    },
    {
      key: 'planCount',
      header: 'Plans',
      sortable: true,
      align: 'right' as const,
      render: (p) => <span className="text-xs text-slate-600">{p.planCount ?? 0}</span>,
    },
    ];
    return all.filter((c) => {
    // A peptides-only clinic has no referral sources and no plans; the columns
    // are dead width, and a blank one reads as missing data rather than as a
    // surface they never use.
    if (c.key === 'tags') return sections.show(PATIENT_SECTIONS.tags);
    if (c.key === 'referralSourceName') return sections.show(PATIENT_SECTIONS.referralSource);
      if (c.key === 'planCount') return sections.show(PATIENT_SECTIONS.plans);
      return true;
    });
  }, [selectPatient, sections]);

  // ── Row actions ─────────────────────────────────────────────────────────────

  const rowActions = useMemo<DataTableAction<PatientSummary>[]>(() => {
    const all: DataTableAction<PatientSummary>[] = [
    {
      label: 'View Profile',
      onClick: (p) => navigateToPatient(p),
    },
    {
      label: 'Create Plan',
      onClick: (p) => navigate(`/plans/new?patientId=${p.patientId}`),
    },
    {
      label: 'Send SMS',
      onClick: () => {
        toast.info('Send SMS', 'SMS functionality coming soon.');
      },
    },
    {
      label: 'Send Email',
      onClick: () => {
        toast.info('Send Email', 'Email functionality coming soon.');
      },
    },
    ];
    return all.filter((a) => {
    if (a.label === 'Create Plan') return sections.show(PATIENT_SECTIONS.createPlan);
    if (a.label === 'Send SMS') return sections.show(PATIENT_SECTIONS.sendSms);
      if (a.label === 'Send Email') return sections.show(PATIENT_SECTIONS.sendEmail);
      return true;
    });
  }, [navigate, navigateToPatient, sections]);

  // ── Render ────────────────────────────────────────────────────────────────

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
    <ListPageLayout
      filterSidebar={sections.hidden(PATIENT_SECTIONS.filters) ? undefined : (
        <FilterSidebar
          filters={filterConfigs}
          values={effectiveValues}
          options={sidebarOptions}
          negations={filterState.negations}
          onChange={filterState.setFilter}
          onNegateChange={filterState.setNegation}
          onClear={filterState.clearFilter}
          searchCommitMode="blur-or-enter"
          showAIAssistant={false}
          storageKey="patients"
          lockedKeys={isFacilityLocked ? ['facility'] : undefined}
        />
      )}
      // Every tap on a row bumps this, including a re-tap of the already-selected
      // patient. That re-tap is invisible to the layout's node-presence fallback —
      // `rightPanel != null` is already true — which is why dismissing the sheet used
      // to make the page un-openable until reload (SCRUM-1383).
      detailOpenSignal={panelExpandTrigger}
      rightPanel={selectedPatient ? (
        <DetailRightPanel expandTrigger={panelExpandTrigger}>
          <div className="flex h-full min-h-0 w-full min-w-0 flex-col bg-white">
            <div className="flex items-center justify-end gap-1 border-b border-slate-200 px-3 py-1.5">
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0"
                onClick={() => navigateToPatient(selectedPatient)}
              >
                <ExternalLink className="mr-1 h-3.5 w-3.5" />
                Open
              </Button>
            </div>
            <div className="min-h-0 flex-1">
              <DetailProfile
                detail={detail}
                loading={detailLoading}
                seedPatient={selectedPatient}
                onSectionNavigate={(section) =>
                  selectedPatient?.patientId && navigate(`/patients/${selectedPatient.patientId}?section=${section}`)
                }
              />
            </div>
          </div>
        </DetailRightPanel>
      ) : undefined}
    >
      <div className="flex h-full min-h-0 flex-col max-md:h-auto">
        <div className="shrink-0 space-y-6 p-6">
          <PageHeader
            variant="compact"
            title="Patients"
            actions={(
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <Switch
                    checked={showDeleted}
                    onChange={(checked) => {
                      setShowDeleted(checked);
                      setDeletedPage(1);
                    }}
                  />
                  Show Deleted
                </label>
                <Button
                  variant="secondary"
                  leftIcon={<Download className="h-4 w-4" />}
                  disabled={total === 0 || exporting}
                  loading={exporting}
                  onClick={handleExport}
                >
                  Export
                </Button>
                {exportModal}
                {hasPermission(20) && (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Megaphone className="h-4 w-4" />}
                    onClick={() => setMarketingModalOpen(true)}
                  >
                    Email / SMS List
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => navigate('/leads/new')}
                >
                  Add Patient
                </Button>
              </div>
            )}
          />

          <MetricCardGrid columns={4}>
            <StatCard label="Total Patients" value={kpis?.total ?? 0} />
            <StatCard label="Active Patients" value={kpis?.active ?? 0} />
            <StatCard label="New Patients" value={kpis?.new ?? 0} />
            <StatCard label="Cold Patients" value={kpis?.cold ?? 0} />
          </MetricCardGrid>

        </div>

        <div className="min-h-0 flex-1 overflow-auto max-md:overflow-visible">
          {!showDeleted ? (
            <DataTable<PatientSummary>
              data={patients}
              columns={columns}
              keyField="patientId"
              loading={isLoading}
              rowActions={rowActions}
              onRowClick={selectPatient}
              onRowMouseEnter={prefetchPatientRow}
              page={page}
              pageSize={perPage}
              total={total}
              onPageChange={setPage}
              selectedIds={selectedPatient ? new Set([selectedPatient.patientId]) : undefined}
              manualSort
              sortKey={sortBy || null}
              sortDir={sortDir}
              onSortChange={handleSortChange}
              emptyTitle="No patients found"
              emptyDescription={search ? `No patients found for "${search}".` : 'Try adjusting the filters.'}
            />
          ) : (
            <DataTable<PatientSummary>
              data={deletedData?.data ?? []}
              columns={columns}
              keyField="patientId"
              loading={isDeletedLoading}
              page={deletedPage}
              pageSize={perPage}
              total={deletedData?.meta?.total ?? 0}
              onPageChange={setDeletedPage}
              emptyTitle="No deleted patients"
              emptyDescription="No deleted patients found."
            />
          )}
        </div>
      </div>
    </ListPageLayout>

      {hasPermission(20) && (
        <MarketingActionsModal
          isOpen={marketingModalOpen}
          onClose={() => setMarketingModalOpen(false)}
          sourceType="patient"
          filterConditions={marketingFilterConditions}
        />
      )}
    </>
  );
}
